import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ResultKey,
  TranscriptionFile,
  WorkRecord,
  RESULT_META,
  RESULT_ORDER,
  STRUCTURE_COLUMN_LABELS,
  availableResults,
  detectFileKind,
  fileMeta,
  upsertWork,
  validateFile,
  workStatus,
} from "./core";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { DataIcon } from "./icons";
import { PRICE_RULES_HTML } from "./mock";
import {
  API_RESULT_BY_KEY,
  applyTaskMetadata,
  compactDuration,
  fileKindFromAssetType,
  fromApiResultType,
  isRunningStatus,
  isTerminalStatus,
  mergeProgress,
  pickQuote,
  quoteFromApi,
  taskToWork,
  templateToWork,
  toApiResultTypes,
  apiErrorMessage,
} from "./adapter";
import {
  buildAiTranscriptionFormData,
  buildAiTranscriptionPointPreviewFormData,
  createAiTranscriptionRequestId,
  createAiTranscriptionTaskApi,
  deleteAiTranscriptionTaskApi,
  exportAiTranscriptionTaskApi,
  getAiTranscriptionTaskApi,
  getAiTranscriptionTasksApi,
  getAiTranscriptionTemplateApi,
  getAiTranscriptionTemplatesApi,
  operateAiTranscriptionTaskApi,
  previewAiTranscriptionPointApi,
  quoteAiTranscriptionTaskApi,
} from "../../service/aiTranscription";
import { triggerPointsRefresh } from "../../service/points";
import { toProxiedDownloadUrl } from "../../utils/videoUrl";
import "./Transcription.scss";

type ViewMode = "compose" | "result";
type GalleryTab = "examples" | "works";

interface PageState {
  file: TranscriptionFile | null;
  selected: ResultKey[];
  prompt: string;
  cache: { original?: boolean; video?: boolean };
  results: WorkRecord["results"];
  active: ResultKey;
  view: ViewMode;
  supplement: boolean;
  force: ResultKey | null;
  job: WorkRecord["job"];
  charged: number;
  works: WorkRecord[];
  workId: string | null;
  detailWorkId: string | null;
  gallery: GalleryTab;
  templatePreview: WorkRecord | null;
  templates: AiTranscription.Template[];
  createdAt?: number;
  warnings?: string[];
  lastKeys?: ResultKey[];
  taskStatus?: string;
  progress?: number;
  currentStep?: string;
}

const INITIAL_STATE: PageState = {
  file: null,
  selected: [],
  prompt: "",
  cache: {},
  results: {},
  active: "summary",
  view: "compose",
  supplement: false,
  force: null,
  job: null,
  charged: 0,
  works: [],
  workId: null,
  detailWorkId: null,
  gallery: "examples",
  templatePreview: null,
  templates: [],
};

function unwrapData<T>(res: AiTranscription.CommonResult<T> | T): T {
  if (res && typeof res === "object" && "data" in (res as object)) {
    return (res as AiTranscription.CommonResult<T>).data;
  }
  return res as T;
}

function taskIdOf(workId?: string | null): number | null {
  const id = Number(workId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function optionTitle(key: ResultKey, fileType?: TranscriptionFile["type"]) {
  return key === "summary" && fileType === "video" ? "视频总结" : RESULT_META[key][0];
}

function exportFilename(work: WorkRecord, keys?: ResultKey[]) {
  const base = (work.file?.name || "AI转录").replace(/\.[^.]+$/, "").replace(/[\\/:*?"<>|]/g, "_");
  return `${base}${keys ? `-${optionTitle(keys[0], work.file?.type)}` : "-全部结果"}.txt`;
}

function TemplateCover({ template }: { template: AiTranscription.Template }) {
  const kind = fileKindFromAssetType(template.assetType);
  const duration =
    kind === "doc"
      ? `${Number(template.characterCount || 0).toLocaleString()} 字符`
      : compactDuration(template.durationMillis);
  const typeLabel = kind === "video" ? "视频转录" : kind === "audio" ? "音频转录" : "文档提取";
  const typeIcon = kind === "audio" ? "headphones" : kind === "video" ? "video" : "file";

  return (
    <span className="template-cover">
      {template.coverUrl ? <img className="cover-art" src={template.coverUrl} alt="" /> : null}
      <span className="template-type">
        <DataIcon name={typeIcon} />
        {typeLabel}
      </span>
      {duration ? <span className="template-duration">{duration}</span> : null}
    </span>
  );
}

export const AiTranscription: FC = () => {
  const [state, setState] = useState<PageState>(INITIAL_STATE);
  const [fileError, setFileError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTitle, setInfoTitle] = useState("");
  const [infoHtml, setInfoHtml] = useState("");
  const [usageOpen, setUsageOpen] = useState(false);
  const [supplementOpen, setSupplementOpen] = useState(false);
  const [supplementDraft, setSupplementDraft] = useState<{
    workId: string;
    keys: ResultKey[];
    force: ResultKey | null;
    prompt: string;
    mode: AiTranscription.OperationMode;
  } | null>(null);
  const [supplementError, setSupplementError] = useState("");
  const [supplementQuote, setSupplementQuote] = useState(quoteFromApi(null));
  const [supplementQuoteLoading, setSupplementQuoteLoading] = useState(false);
  const [supplementQuoteReady, setSupplementQuoteReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quotePreview, setQuotePreview] = useState<AiTranscription.Quote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const worksSectionRef = useRef<HTMLElement>(null);
  const infoDialogRef = useRef<HTMLDialogElement>(null);
  const usageDialogRef = useRef<HTMLDialogElement>(null);
  const supplementDialogRef = useRef<HTMLDialogElement>(null);
  const uploadFileRef = useRef<File | null>(null);
  const requestIdRef = useRef("");
  const previewSerial = useRef(0);
  const pollInFlight = useRef(false);
  const displayedProgress = useRef<Map<string, number>>(new Map());
  const toastTimer = useRef<number>(0);
  const copiedTimer = useRef<number>(0);
  const pollTimer = useRef<number>(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3000);
  }, []);

  const persist = useCallback((next: PageState | ((prev: PageState) => PageState)) => {
    setState(next);
  }, []);

  const applyDisplayedProgress = useCallback((work: WorkRecord): WorkRecord => {
    if (!isRunningStatus(work.taskStatus)) {
      displayedProgress.current.delete(work.workId);
      return work;
    }
    const next = mergeProgress(displayedProgress.current.get(work.workId), work.progress);
    displayedProgress.current.set(work.workId, next);
    return { ...work, progress: next };
  }, []);

  const stopPolling = useCallback(() => {
    window.clearInterval(pollTimer.current);
    pollTimer.current = 0;
  }, []);

  const loadWorks = useCallback(async () => {
    const list = unwrapData(await getAiTranscriptionTasksApi()) || [];
    return (Array.isArray(list) ? list : []).map((task) => applyDisplayedProgress(taskToWork(task)));
  }, [applyDisplayedProgress]);

  const refreshDetail = useCallback(
    async (workId: string) => {
      const id = taskIdOf(workId);
      if (!id) return null;
      const task = unwrapData(await getAiTranscriptionTaskApi(id));
      return applyDisplayedProgress(taskToWork(task));
    },
    [applyDisplayedProgress],
  );

  const startPolling = useCallback(() => {
    stopPolling();
    const tick = async () => {
      if (pollInFlight.current) return;
      pollInFlight.current = true;
      try {
        let works = await loadWorks();
        const current = stateRef.current;
        const detailId = current.templatePreview ? null : current.detailWorkId;
        if (detailId) {
          const detailWork = works.find((work) => work.workId === detailId);
          if (detailWork) {
            const fresh = await refreshDetail(detailId);
            if (fresh) works = upsertWork(works, fresh);
          }
        }
        setState((prev) => ({
          ...prev,
          works: works.map((work) => {
            const existing = prev.works.find((item) => item.workId === work.workId);
            return existing ? { ...work, active: existing.active } : work;
          }),
        }));
        if (!works.some((work) => isRunningStatus(work.taskStatus))) stopPolling();
      } catch {
        stopPolling();
      } finally {
        pollInFlight.current = false;
      }
    };
    pollTimer.current = window.setInterval(tick, 5000);
    void tick();
  }, [loadWorks, refreshDetail, stopPolling]);

  useEffect(() => {
    const bind = (el: HTMLDialogElement | null, open: boolean) => {
      if (!el) return;
      if (open) {
        if (!el.open) el.showModal();
      } else if (el.open) {
        el.close();
      }
    };
    bind(infoDialogRef.current, infoOpen);
    bind(usageDialogRef.current, usageOpen);
    bind(supplementDialogRef.current, supplementOpen);
  }, [infoOpen, usageOpen, supplementOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [templateRes, works] = await Promise.all([
          getAiTranscriptionTemplatesApi(),
          loadWorks(),
        ]);
        if (cancelled) return;
        const templates = unwrapData(templateRes) || [];
        setState((prev) => ({
          ...prev,
          templates: Array.isArray(templates) ? templates : [],
          works,
        }));
        if (works.some((work) => isRunningStatus(work.taskStatus))) startPolling();
      } catch (error) {
        if (!cancelled) showToast(apiErrorMessage(error, "加载转录数据失败"));
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
      window.clearTimeout(toastTimer.current);
      window.clearTimeout(copiedTimer.current);
    };
  }, [loadWorks, showToast, startPolling, stopPolling]);

  const quoteResult = useMemo(
    () => quoteFromApi(pickQuote(quotePreview, state.selected)),
    [quotePreview, state.selected],
  );

  const emptyDetail = useMemo(
    () =>
      templateToWork({
        id: 0,
        name: "",
        assetType: "DOCUMENT",
      }),
    [],
  );

  const detail = useMemo(() => {
    if (state.templatePreview) return state.templatePreview;
    return state.works.find((w) => w.workId === state.detailWorkId) || emptyDetail;
  }, [emptyDetail, state.detailWorkId, state.templatePreview, state.works]);

  const startInvalid =
    !state.file ||
    !uploadFileRef.current ||
    !state.selected.length ||
    (state.selected.includes("rewrite") && !state.prompt.trim()) ||
    quoteLoading ||
    submitting ||
    !quoteResult.total;

  const openInfo = (title: string, html: string) => {
    setInfoTitle(title);
    setInfoHtml(html);
    setInfoOpen(true);
  };

  const queuePointPreview = useCallback(
    async (
      file: File,
      selected: ResultKey[],
      prompt: string,
      meta: { type: TranscriptionFile["type"]; seconds: number },
    ) => {
      if (!file || !selected.length) {
        setQuotePreview(null);
        setQuoteLoading(false);
        return;
      }
      const serial = ++previewSerial.current;
      setQuoteLoading(true);
      setQuotePreview(null);
      setFileError("");
      try {
        const form = buildAiTranscriptionPointPreviewFormData({
          resultTypes: toApiResultTypes(selected),
          rewriteRequirement: prompt,
          assetType: meta.type === "video" ? "VIDEO" : meta.type === "audio" ? "AUDIO" : undefined,
          durationSeconds: meta.seconds,
          file: meta.type === "doc" ? file : null,
        });
        const preview = unwrapData(await previewAiTranscriptionPointApi(form));
        if (serial !== previewSerial.current || uploadFileRef.current !== file) return;
        setQuotePreview(preview || null);
        if (preview?.metadata) {
          setState((prev) =>
            prev.file ? { ...prev, file: applyTaskMetadata(prev.file, preview.metadata) } : prev,
          );
        }
      } catch (error) {
        if (serial !== previewSerial.current || uploadFileRef.current !== file) return;
        setQuotePreview(null);
        setFileError(apiErrorMessage(error, "积分预估失败"));
      } finally {
        if (serial === previewSerial.current) setQuoteLoading(false);
      }
    },
    [],
  );

  const selectFile = (file: TranscriptionFile, raw?: File | null, extras?: Partial<PageState>) => {
    if (raw) uploadFileRef.current = raw;
    requestIdRef.current = createAiTranscriptionRequestId();
    setQuotePreview(null);
    const available = availableResults(file);
    let previewKeys: ResultKey[] = ["summary"];
    let previewPrompt = extras?.prompt ?? "";
    persist((prev) => {
      const selected = (extras?.selected?.length ? extras.selected : prev.selected)
        .filter((key) => available.includes(key));
      previewKeys = selected.length ? selected : ["summary"];
      previewPrompt = extras?.prompt !== undefined ? extras.prompt : prev.prompt;
      return {
        ...prev,
        templatePreview: null,
        workId: null,
        detailWorkId: null,
        warnings: [],
        lastKeys: undefined,
        file,
        selected: previewKeys,
        cache: {},
        results: {},
        prompt: previewPrompt,
        job: null,
        charged: 0,
        supplement: false,
        force: null,
        view: "compose",
        taskStatus: undefined,
        progress: undefined,
        currentStep: undefined,
      };
    });
    setFileError("");
    if (raw) void queuePointPreview(raw, previewKeys, previewPrompt, { type: file.type, seconds: file.seconds });
  };

  const resetCompose = useCallback(() => {
    previewSerial.current += 1;
    uploadFileRef.current = null;
    requestIdRef.current = "";
    setQuotePreview(null);
    setQuoteLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFileError("");
  }, []);

  const resetFile = () => {
    resetCompose();
    persist((prev) => ({
      ...prev,
      templatePreview: null,
      workId: null,
      detailWorkId: null,
      warnings: [],
      lastKeys: undefined,
      file: null,
      selected: [],
      cache: {},
      results: {},
      prompt: "",
      job: null,
      charged: 0,
      supplement: false,
      force: null,
      view: "compose",
      taskStatus: undefined,
      progress: undefined,
      currentStep: undefined,
    }));
  };

  const readMediaDuration = (file: File, kind: "video" | "audio") =>
    new Promise<number>((resolve, reject) => {
      const media = document.createElement(kind);
      const url = URL.createObjectURL(file);
      const timeout = window.setTimeout(() => finish(NaN), 15000);
      const finish = (seconds: number) => {
        window.clearTimeout(timeout);
        media.onloadedmetadata = null;
        media.onerror = null;
        media.removeAttribute("src");
        media.load();
        URL.revokeObjectURL(url);
        if (Number.isFinite(seconds) && seconds > 0) resolve(seconds);
        else reject(new Error(kind === "video" ? "无法读取视频时长" : "无法读取音频时长"));
      };
      media.preload = "metadata";
      media.muted = true;
      media.onloadedmetadata = () => finish(media.duration);
      media.onerror = () => finish(NaN);
      media.src = url;
    });

  const handleFiles = async (files: FileList | File[], template?: AiTranscription.Template) => {
    const list = Array.from(files);
    if (list.length !== 1) {
      setFileError("一次只能上传一个文件。");
      return;
    }
    const file = list[0];
    if (!file) return;
    const err = validateFile(file.name, file.size);
    if (err) {
      setFileError(err);
      return;
    }
    const type = detectFileKind(file.name) || fileKindFromAssetType(template?.assetType);
    if (!type) return;
    setFileError("");
    try {
      let seconds = Number(template?.durationMillis || 0) / 1000;
      if ((type === "video" || type === "audio") && !seconds) {
        seconds = await readMediaDuration(file, type);
        if (type === "video") {
          const millis = Math.round(seconds * 1000);
          console.log(
            `[AI转录] 视频时长 ${seconds} 秒 (${millis} 毫秒)`,
            { name: file.name, seconds, millis },
          );
        }
      }
      const templateSelected = (template?.defaultResultTypes || [])
        .map((item) => fromApiResultType(item))
        .filter((key): key is ResultKey => !!key);
      selectFile(
        {
          name: file.name,
          bytes: file.size,
          seconds,
          type,
          chars: template?.characterCount,
          pages: template?.pageCount,
          sample: false,
        },
        file,
        template
          ? {
              selected: templateSelected.length ? templateSelected : ["summary"],
              prompt: template.defaultRewriteRequirement || "",
            }
          : undefined,
      );
    } catch (error: any) {
      setFileError(error?.message || "文件读取失败，请重新选择。");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const start = async () => {
    const file = uploadFileRef.current;
    if (!file || startInvalid) return;
    if (state.selected.includes("rewrite") && !state.prompt.trim()) {
      showToast("请填写重写要求。");
      return;
    }
    setSubmitting(true);
    try {
      const requestId = requestIdRef.current || createAiTranscriptionRequestId();
      requestIdRef.current = requestId;
      const form = buildAiTranscriptionFormData({
        file,
        resultTypes: toApiResultTypes(state.selected),
        rewriteRequirement: state.prompt,
        requestId,
      });
      unwrapData(await createAiTranscriptionTaskApi(form));
      triggerPointsRefresh();
      resetCompose();
      const works = await loadWorks();
      setState((prev) => ({
        ...prev,
        works,
        gallery: "works",
        view: "compose",
        templatePreview: null,
        workId: null,
        detailWorkId: null,
        warnings: [],
        lastKeys: undefined,
        file: null,
        selected: [],
        cache: {},
        results: {},
        prompt: "",
        job: null,
        charged: 0,
        supplement: false,
        force: null,
        taskStatus: undefined,
        progress: undefined,
        currentStep: undefined,
      }));
      startPolling();
      window.setTimeout(() => worksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
      showToast("任务已进入队列，可在我的作品中查看进度");
    } catch (error) {
      showToast(apiErrorMessage(error, "提交失败"));
      setFileError(apiErrorMessage(error, "提交失败"));
    } finally {
      setSubmitting(false);
    }
  };

  const openWork = async (id: string) => {
    try {
      const work = await refreshDetail(id);
      if (!work) return;
      setState((prev) => ({
        ...prev,
        works: upsertWork(prev.works, work),
        templatePreview: null,
        detailWorkId: work.workId,
        view: "result",
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (isRunningStatus(work.taskStatus)) startPolling();
    } catch (error) {
      showToast(apiErrorMessage(error, "加载作品失败"));
    }
  };

  const openTemplate = async (id: number) => {
    try {
      const template = unwrapData(await getAiTranscriptionTemplateApi(id));
      persist((prev) => ({
        ...prev,
        templates: prev.templates.some((item) => item.id === template.id)
          ? prev.templates.map((item) => (item.id === template.id ? template : item))
          : [...prev.templates, template],
        templatePreview: templateToWork(template),
        view: "result",
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      showToast(apiErrorMessage(error, "加载模板失败"));
    }
  };

  const useTemplate = async () => {
    const templateId = state.templatePreview?.templateId;
    const template = state.templates.find((item) => item.id === templateId) || null;
    if (!template) return;
    if (!template.sourceUrl || String(template.sourceUrl).startsWith("__REPLACE")) {
      showToast("模板素材地址尚未配置");
      return;
    }
    setSubmitting(true);
    try {
      const url = toProxiedDownloadUrl(template.sourceUrl) || template.sourceUrl;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`模板素材下载失败 (${response.status})`);
      const blob = await response.blob();
      const file = new File([blob], template.filename || template.name || "template", {
        type: template.mimeType || blob.type || "application/octet-stream",
      });
      await handleFiles([file], template);
      window.scrollTo({ top: 0, behavior: "smooth" });
      showToast("模板参数和素材已回填");
    } catch (error) {
      showToast(apiErrorMessage(error, "模板素材下载失败"));
    } finally {
      setSubmitting(false);
    }
  };

  const showWorks = async () => {
    try {
      const works = await loadWorks();
      setState((prev) => ({
        ...prev,
        works,
        templatePreview: null,
        view: "compose",
        gallery: "works",
      }));
    } catch (error) {
      showToast(apiErrorMessage(error, "加载作品失败"));
      persist((prev) => ({ ...prev, templatePreview: null, view: "compose", gallery: "works" }));
    }
    window.setTimeout(() => worksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const backFromDetail = () => {
    setCopied(false);
    window.clearTimeout(copiedTimer.current);
    if (!state.templatePreview) {
      void showWorks();
      return;
    }
    persist((prev) => ({ ...prev, templatePreview: null, view: "compose", gallery: "examples" }));
    window.setTimeout(() => worksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const openSupplement = (key?: ResultKey) => {
    if (detail.isTemplate || !isTerminalStatus(detail.taskStatus)) return;
    const current = key ? detail.results[key] : null;
    const mode: AiTranscription.OperationMode =
      key === "rewrite" && current?.status === "success"
        ? "REWRITE_ADJUST"
        : current && (current.status === "failed" || current.status === "skipped")
          ? "RETRY"
          : "SUPPLEMENT";
    setSupplementDraft({
      workId: detail.workId,
      keys: key ? [key] : [],
      force: key || null,
      prompt: current?.prompt || detail.prompt || "",
      mode,
    });
    setSupplementQuote(quoteFromApi(null));
    setSupplementQuoteReady(false);
    setSupplementError("");
    setSupplementOpen(true);
  };

  useEffect(() => {
    const draft = supplementDraft;
    const id = taskIdOf(draft?.workId);
    if (!supplementOpen || !draft || !id || !draft.keys.length) {
      if (!draft?.keys.length) {
        setSupplementQuote(quoteFromApi(null));
        setSupplementQuoteReady(false);
      }
      return undefined;
    }
    let cancelled = false;
    setSupplementQuoteReady(false);
    setSupplementQuoteLoading(true);
    setSupplementError("");
    quoteAiTranscriptionTaskApi(id, toApiResultTypes(draft.keys))
      .then((res) => {
        if (cancelled) return;
        setSupplementQuote(quoteFromApi(unwrapData(res)));
        setSupplementQuoteReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setSupplementQuote(quoteFromApi(null));
        setSupplementQuoteReady(false);
        setSupplementError(apiErrorMessage(error, "报价失败"));
      })
      .finally(() => {
        if (!cancelled) setSupplementQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [supplementDraft, supplementOpen]);

  const submitSupplement = async () => {
    const draft = supplementDraft;
    const id = taskIdOf(draft?.workId);
    if (!draft || !id || !draft.keys.length || supplementQuoteLoading || !supplementQuoteReady) return;
    if (draft.keys.includes("rewrite") && !draft.prompt.trim()) return;
    try {
      unwrapData(
        await operateAiTranscriptionTaskApi(id, {
          mode: draft.mode,
          resultTypes: toApiResultTypes(draft.keys),
          rewriteRequirement: draft.prompt.trim() || null,
          requestId: createAiTranscriptionRequestId(),
        }),
      );
      triggerPointsRefresh();
      setSupplementOpen(false);
      setSupplementDraft(null);
      const work = await refreshDetail(String(id));
      const works = await loadWorks();
      setState((prev) => ({
        ...prev,
        works: work ? upsertWork(works, work) : works,
        detailWorkId: String(id),
        templatePreview: null,
        view: "result",
        gallery: "works",
      }));
      startPolling();
      showToast(draft.mode === "REWRITE_ADJUST" ? "重写任务已进入队列" : "补充任务已进入队列");
    } catch (error) {
      setSupplementError(apiErrorMessage(error, "补充生成失败"));
    }
  };

  const downloadResults = async (keys?: ResultKey[]) => {
    const id = taskIdOf(detail.workId);
    if (!id || detail.isTemplate) return;
    try {
      await exportAiTranscriptionTaskApi({
        id,
        scope: keys ? "CURRENT" : "ALL",
        resultType: keys ? API_RESULT_BY_KEY[keys[0]] : undefined,
        filename: exportFilename(detail, keys),
      });
      setInfoOpen(false);
      showToast("已导出 TXT 文件");
    } catch (error) {
      showToast(apiErrorMessage(error, "导出失败"));
    }
  };

  const removeWork = async (id: string) => {
    const work = state.works.find((item) => item.workId === id);
    if (work && !isTerminalStatus(work.taskStatus)) {
      showToast("任务处理中，完成后才可删除。");
      return false;
    }
    const taskId = taskIdOf(id);
    if (!taskId) return false;
    try {
      unwrapData(await deleteAiTranscriptionTaskApi(taskId));
      triggerPointsRefresh();
      setState((prev) => ({
        ...prev,
        works: prev.works.filter((item) => item.workId !== id),
        detailWorkId: prev.detailWorkId === id ? null : prev.detailWorkId,
        templatePreview: null,
        view: "compose",
        gallery: "works",
      }));
      return true;
    } catch (error) {
      showToast(apiErrorMessage(error, "删除失败"));
      return false;
    }
  };

  const copyActive = async () => {
    const r = detail.results[detail.active];
    if (!r?.plain) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(r.plain);
      else {
        const ta = document.createElement("textarea");
        ta.value = r.plain;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        if (!ok) throw new Error();
      }
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
      showToast("已复制");
    } catch {
      setCopied(false);
      showToast("复制失败，请选中文字后手动复制。");
    }
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".detail-more-wrap")) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey as any);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey as any);
    };
  }, []);

  const choices = availableResults(state.file);
  const resultKeys = RESULT_ORDER.filter(
    (k) => detail.results[k] || detail.job?.keys.includes(k) || detail.selected?.includes(k),
  );
  const activeResult = detail.results[detail.active] || { status: "pending" as const };
  const structureRows = detail.active === "structure" && activeResult.status === "success"
    ? activeResult.structureRows || []
    : [];
  const showStructureLocation = structureRows.some((row) => row.location.trim());
  const showStructureRole = structureRows.some((row) => row.expressionRole.trim());
  const structureColumnCount = 2 + Number(showStructureLocation) + Number(showStructureRole);
  const canSupplement =
    !detail.isTemplate && availableResults(detail.file).some((key) => detail.results[key]?.status !== "success");
  const progress = workStatus(detail);
  const rewriteInvalid = state.selected.includes("rewrite") && !state.prompt.trim();
  const busy = submitting || !!detail.job;

  const onGalleryKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const tabs: GalleryTab[] = ["examples", "works"];
    const next =
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? tabs.length - 1
          : (index + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length;
    persist((prev) => ({ ...prev, gallery: tabs[next] }));
  };

  const updateActive = (k: ResultKey) => {
    setCopied(false);
    window.clearTimeout(copiedTimer.current);
    persist((prev) => {
      if (prev.templatePreview) {
        return {
          ...prev,
          templatePreview: { ...prev.templatePreview, active: k },
        };
      }
      return {
        ...prev,
        works: prev.works.map((w) => (w.workId === prev.detailWorkId ? { ...w, active: k } : w)),
      };
    });
  };

  return (
    <div className="ai-transcription-page">
      <div className="main">
        <div className={`page-shell${state.view === "result" ? " detail-active" : ""}`}>
          <section className="page-heading">
            <div>
              <span className="section-kicker">AI Transcription</span>
              <h1>AI 转录</h1>
              <p>从视频、音频到文档，直接获得你需要的内容。</p>
            </div>
          </section>

          {state.view === "compose" ? (
            <section className="compose-grid">
              <section className="panel composer-panel">
                <div className="upload-panel">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp4,.mov,.mp3,.wav,.m4a,.pdf,.docx,.txt"
                    hidden
                    onChange={(e) => e.target.files && handleFiles(e.target.files)}
                  />
                  {!state.file ? (
                    <button
                      type="button"
                      className={`dropzone${dragging ? " dragging" : ""}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragging(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragging(false);
                        handleFiles(e.dataTransfer.files);
                      }}
                    >
                      <DataIcon name="cloudUpload" className="upload-icon" />
                      <strong>点击上传或拖拽文件到此处</strong>
                      <span className="upload-limits">支持视频、音频、文档 · 一次一个文件</span>
                    </button>
                  ) : (
                    <div className="file-card">
                      <span className={`sample-icon ${state.file.type === "video" ? "blue" : state.file.type === "audio" ? "amber" : "green"}`}>
                        <DataIcon
                          name={
                            state.file.type === "audio"
                              ? "headphones"
                              : state.file.type === "video"
                                ? "video"
                                : "file"
                          }
                        />
                      </span>
                      <div className="file-info">
                        <h3 title={state.file.name}>{state.file.name}</h3>
                        <p>{fileMeta(state.file)}</p>
                        <span className="file-status">
                          {quoteLoading
                            ? "服务端正在校验并预估积分"
                            : quoteResult.total
                              ? "服务端校验通过"
                              : fileError || "等待选择结果"}
                        </span>
                      </div>
                      {!state.supplement ? (
                        <div className="file-buttons">
                          <button
                            type="button"
                            className="icon-button"
                            aria-label="更换文件"
                            title="更换文件"
                            disabled={submitting}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <DataIcon name="refresh" />
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            id="removeFile"
                            aria-label="移除文件"
                            title="移除文件"
                            disabled={submitting}
                            onClick={resetFile}
                          >
                            <DataIcon name="trash" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                  {fileError ? (
                    <div className="message error" role="alert">
                      {fileError}
                    </div>
                  ) : null}
                </div>

                <section className="selection-panel" aria-labelledby="selectionHeading">
                  <div className="section-title">
                    <div className="selection-heading">
                      <h2 id="selectionHeading">
                        {state.force ? "重新生成" : state.supplement ? "补充生成" : "选择所需结果"}
                      </h2>
                      <span className="selection-subtitle">{state.file ? "可多选" : "上传后可多选"}</span>
                    </div>
                    <div className="selection-tools">
                      <button type="button" className="text-button small" id="limitButton" onClick={() => setUsageOpen(true)}>
                        使用说明
                        <DataIcon name="info" />
                      </button>
                    </div>
                  </div>
                  <div className="options">
                    {choices.map((k) => {
                      const complete =
                        state.supplement &&
                        state.results[k]?.status === "success" &&
                        state.force !== k;
                      const disabled = complete || submitting;
                      const checked = state.selected.includes(k);
                      return (
                        <label
                          key={k}
                          title={complete ? "已生成，可在结果页重新生成" : RESULT_META[k][1]}
                          className={`option${checked ? " selected" : ""}${disabled ? " disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            value={k}
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...state.selected, k]
                                : state.selected.filter((x) => x !== k);
                              persist((prev) => ({ ...prev, selected: next }));
                            }}
                          />
                          <span>
                            {optionTitle(k, state.file?.type)}
                            {complete ? " · 已生成" : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {state.selected.includes("rewrite") ? (
                    <div className="rewrite-field">
                      <label htmlFor="rewritePrompt">
                        重写要求 <span className="required">*</span>
                      </label>
                      <textarea
                        id="rewritePrompt"
                        rows={3}
                        maxLength={300}
                        disabled={submitting}
                        placeholder="例如：改成自然的短视频口播，约300字，保留核心观点。"
                        value={state.prompt}
                        onChange={(e) => persist((prev) => ({ ...prev, prompt: e.target.value }))}
                      />
                      <span className="field-note">{state.prompt.length} / 300</span>
                    </div>
                  ) : null}
                  <p className={`start-hint${rewriteInvalid ? " invalid" : ""}`}>
                    {rewriteInvalid ? "请填写重写要求" : "无需确认原文，直接获得结果"}
                  </p>
                  <div className="submit-row">
                    <details
                      className={`cost-box${state.file && state.selected.length && quoteResult.total ? "" : " is-hidden"}`}
                    >
                      <summary
                        className="points-badge"
                        aria-label="预计积分，点击查看明细"
                        title="按当前参数预估，实际以扣减为准"
                      >
                        <CoinsEstimateIcon />
                        <span>
                          预计消耗 <span className="cost-point">{quoteResult.total}</span> 积分
                        </span>
                      </summary>
                      <div className="cost-popover">
                        <div className="cost-popover-head">
                          <strong>积分预估</strong>
                          <button
                            type="button"
                            className="text-button"
                            onClick={() => openInfo("积分预估规则", PRICE_RULES_HTML)}
                          >
                            计费规则
                            <DataIcon name="info" />
                          </button>
                        </div>
                        <div className="cost-lines">
                          {quoteResult.lines.map((l) => (
                            <div key={l.key}>
                              <span>{l.label}</span>
                              <span>{`${Number(l.amount || 0).toFixed(2)} 积分`}</span>
                            </div>
                          ))}
                        </div>
                        <p className="cost-footnote">
                          {!state.file
                            ? "选择素材和结果后自动计算"
                            : quoteResult.total
                              ? `合计后向上取整 · 1元 = 10积分${state.supplement ? " · 已有基础内容不重复收费" : ""}`
                              : state.selected.length
                                ? "暂无可执行的收费项"
                                : "请选择至少一项结果"}
                        </p>
                      </div>
                    </details>
                    <div className="submit-actions">
                      <button
                        type="button"
                        className="primary start-button"
                        disabled={startInvalid}
                        onClick={() => start()}
                      >
                        <span>
                          {submitting
                            ? "正在提交"
                            : state.force
                              ? "重新生成"
                              : state.supplement
                                ? "补充生成"
                                : "开始处理"}
                        </span>
                        <DataIcon name="arrow" />
                      </button>
                    </div>
                  </div>
                  {state.supplement ? (
                    <button
                      type="button"
                      className="text-button cancel-supplement"
                      onClick={() => persist((prev) => ({ ...prev, supplement: false, force: null, view: "result" }))}
                    >
                      返回已有结果
                    </button>
                  ) : null}
                </section>
              </section>
            </section>
          ) : null}

          {state.view !== "result" ? (
            <section className="works-section" ref={worksSectionRef} aria-label="素材与作品">
              <header className="works-toolbar">
                <div className="gallery-tabs" role="tablist" aria-label="素材与作品">
                  <button
                    className={`gallery-tab${state.gallery === "examples" ? " active" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={state.gallery === "examples"}
                    aria-controls="examplesPanel"
                    tabIndex={state.gallery === "examples" ? 0 : -1}
                    onClick={() => persist((prev) => ({ ...prev, gallery: "examples" }))}
                    onKeyDown={(e) => onGalleryKey(e, 0)}
                  >
                    模板
                  </button>
                  <button
                    className={`gallery-tab${state.gallery === "works" ? " active" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={state.gallery === "works"}
                    aria-controls="worksPanel"
                    tabIndex={state.gallery === "works" ? 0 : -1}
                    onClick={() => void showWorks()}
                    onKeyDown={(e) => onGalleryKey(e, 1)}
                  >
                    我的作品 <span>{state.works.length}</span>
                  </button>
                </div>
                <span className="works-note">
                  {state.gallery === "works" ? "生成进度与结果保存在这里" : "点击模板，查看内容与结果"}
                </span>
              </header>
              {state.gallery === "examples" ? (
                <div className="examples" id="examplesPanel" role="tabpanel">
                  {state.templates.length ? (
                    <div className="template-grid">
                      {state.templates.map((template) => {
                        return (
                          <button
                            key={template.id}
                            type="button"
                            className="template-card"
                            disabled={submitting}
                            aria-label={`查看${template.name || "模板"}`}
                            onClick={() => openTemplate(template.id)}
                          >
                            <TemplateCover template={template} />
                            <span className="template-info">
                              <strong>{template.name}</strong>
                              <span className="template-description">{template.description || ""}</span>
                              <span className="template-bottom">
                                <span>{template.category || ""}</span>
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="works-empty">
                      <DataIcon name="folder" />
                      <h3>暂无可用模板</h3>
                      <p>模板加载后可在这里查看内容与结果。</p>
                    </div>
                  )}
                </div>
              ) : (
                <div id="worksPanel" role="tabpanel">
                  <div className="works-grid">
                    {state.works.map((w) => {
                      const status = workStatus(w);
                      const date = new Date(w.createdAt).toLocaleString("zh-CN", {
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                      const tags = Array.from(
                        new Set([
                          ...RESULT_ORDER.filter((k) => w.results[k]?.status === "success"),
                          ...(w.job?.keys || w.lastKeys || w.selected || []),
                        ]),
                      );
                      return (
                        <article
                          className="work-card"
                          key={w.workId}
                          role="button"
                          tabIndex={0}
                          aria-label={`查看作品：${w.file.name}`}
                          onClick={() => void openWork(w.workId)}
                          onKeyDown={(event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              void openWork(w.workId);
                            }
                          }}
                        >
                          <div className="work-card-head">
                            <span className="work-icon">
                              <DataIcon
                                name={
                                  w.file.type === "video"
                                    ? "video"
                                    : w.file.type === "audio"
                                      ? "headphones"
                                      : "file"
                                }
                              />
                            </span>
                            <span className={`work-state ${status.kind}`}>{status.label}</span>
                          </div>
                          <h3 title={w.file.name}>{w.file.name}</h3>
                          <p className="work-meta">{fileMeta(w.file)}</p>
                          <div className="work-tags">
                            {tags.map((k) => (
                              <span key={k}>{k === "summary" && w.file.type === "video" ? "视频总结" : RESULT_META[k][0]}</span>
                            ))}
                          </div>
                          {w.job ? (
                            <div
                              className="work-progress"
                              role="progressbar"
                              aria-label="生成进度"
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={status.progress}
                            >
                              <span style={{ width: `${status.progress}%` }} />
                            </div>
                          ) : null}
                          <p className="work-message">
                            {status.message}
                            {w.job ? ` · ${status.progress}%` : ""}
                          </p>
                          <div className="work-card-foot">
                            <span>{date}</span>
                            <button
                              type="button"
                              className="text-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                void openWork(w.workId);
                              }}
                            >
                              {w.job ? "查看已完成内容" : "查看结果"}
                              <DataIcon name="chevron" />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  {!state.works.length ? (
                    <div className="works-empty">
                      <DataIcon name="folder" />
                      <h3>还没有转录作品</h3>
                      <p>上传素材并开始处理，进度和结果会保存在这里。</p>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          ) : (
            <section>
              <div className="detail-topbar">
                <button
                  type="button"
                  className="detail-back"
                  aria-label={detail.isTemplate ? "返回模板" : "返回我的作品"}
                  title={detail.isTemplate ? "返回模板" : "返回我的作品"}
                  onClick={backFromDetail}
                >
                  <DataIcon name="chevron" />
                </button>
                <div className="detail-tools">
                  {detail.isTemplate ? (
                    <button
                      type="button"
                      className={`secondary template-use-button${submitting ? " is-loading" : ""}`}
                      disabled={submitting}
                      aria-busy={submitting}
                      onClick={useTemplate}
                    >
                      <DataIcon name={submitting ? "refresh" : "plus"} />
                      {submitting ? "正在应用…" : "使用模板"}
                    </button>
                  ) : null}
                  {!detail.isTemplate ? (
                    <button
                      type="button"
                      className="toolbar-export"
                      disabled={!progress.success}
                      onClick={() => openInfo("导出结果", "")}
                    >
                      <DataIcon name="download" />
                      导出
                    </button>
                  ) : null}
                  {!detail.isTemplate ? (
                    <div className="detail-more-wrap">
                      <button
                        type="button"
                        className="detail-more-button"
                        aria-label="更多作品操作"
                        aria-expanded={moreOpen}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMoreOpen((v) => !v);
                        }}
                      >
                        <DataIcon name="more" />
                      </button>
                      {moreOpen ? (
                        <div className="detail-more-menu">
                          <button
                            type="button"
                            className="detail-delete-button"
                            disabled={!isTerminalStatus(detail.taskStatus)}
                            onClick={() => {
                              if (!isTerminalStatus(detail.taskStatus)) return;
                              const id = detail.workId;
                              setMoreOpen(false);
                              openInfo("删除作品", id);
                            }}
                          >
                            <DataIcon name="trash" />
                            删除作品
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="result-summary">
                <span className="result-file-icon">
                  <DataIcon
                    name={
                      detail.file?.type === "video"
                        ? "video"
                        : detail.file?.type === "audio"
                          ? "headphones"
                          : "file"
                    }
                  />
                </span>
                <h1>{detail.file?.name}</h1>
                <p>
                  {detail.file ? fileMeta(detail.file) : ""}
                  {detail.isTemplate ? " · 模板内容预览" : ` · 累计消耗 ${detail.charged} 积分`}
                </p>
                <span className="status-badge">
                  {detail.isTemplate ? "模板预览" : progress.label}
                </span>
              </div>
              {detail.warnings?.length ? (
                <div id="resultWarning" className="message warning">
                  {detail.warnings.join("；")}
                </div>
              ) : null}
              {detail.job ? (
                <div className="detail-progress" role="status" aria-live="polite">
                  <DataIcon name="sparkles" />
                  <div>
                    <strong>{progress.message}</strong>
                    <p>已有内容可继续阅读，完成后自动更新当前作品。</p>
                  </div>
                  <span>{progress.progress}%</span>
                </div>
              ) : null}
              <div className="result-layout">
                <div className="result-tabs-row">
                  <nav className="result-tabs" aria-label="生成结果">
                    {resultKeys.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={`result-tab${detail.active === k ? " active" : ""}`}
                        aria-pressed={detail.active === k}
                        onClick={() => updateActive(k)}
                      >
                        <DataIcon name={RESULT_META[k][2]} />
                        <span>{k === "original" ? "提取原文" : optionTitle(k, detail.file?.type)}</span>
                        {detail.job?.keys.includes(k) && detail.results[k]?.status !== "success" ? (
                          <small>生成中</small>
                        ) : detail.results[k]?.status !== "success" ? (
                          <small>未完成</small>
                        ) : null}
                      </button>
                    ))}
                  </nav>
                  {canSupplement ? (
                    <button
                      type="button"
                      className="text-button supplement-inline"
                      disabled={busy || !isTerminalStatus(detail.taskStatus)}
                      onClick={() => openSupplement()}
                    >
                      <DataIcon name="plus" />
                      补充生成
                    </button>
                  ) : null}
                </div>
                <article className="panel result-paper">
                  <div className="paper-toolbar">
                    <div>
                      <h2>
                        {detail.active === "original"
                          ? "原始内容"
                          : optionTitle(detail.active, detail.file?.type)}
                      </h2>

                    </div>
                    <div className="paper-actions">
                      {!detail.isTemplate &&
                      detail.active === "rewrite" &&
                      activeResult.status === "success" &&
                      isTerminalStatus(detail.taskStatus) ? (
                        <button
                          type="button"
                          className="text-button"
                          disabled={busy}
                          onClick={() => openSupplement("rewrite")}
                        >
                          <DataIcon name="refresh" />
                          <span>调整重写要求</span>
                        </button>
                      ) : null}
                      {!detail.isTemplate &&
                      isTerminalStatus(detail.taskStatus) &&
                      (activeResult.status === "failed" || activeResult.status === "skipped") ? (
                        <button
                          type="button"
                          className="text-button"
                          disabled={busy}
                          onClick={() => openSupplement(detail.active)}
                        >
                          <DataIcon name="refresh" />
                          <span>重试</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className={`secondary${copied ? " is-copied" : ""}`}
                        disabled={activeResult.status !== "success"}
                        title={copied ? "已复制" : "复制"}
                        onClick={copyActive}
                      >
                        <DataIcon name="copy" />
                        {copied ? "已复制" : "复制"}
                      </button>
                    </div>
                  </div>
                  <div className="result-content">
                    {detail.active === "rewrite" && activeResult.prompt ? (
                      <p className="rewrite-result-note">本次要求：{activeResult.prompt}</p>
                    ) : null}
                    {activeResult.status === "success" ? (
                      detail.active === "structure" ? (
                        structureRows.length ? (
                          <div className="structure-table-wrap">
                            <table className={`structure-table structure-table--${structureColumnCount}`}>
                              <thead>
                                <tr>
                                  <th className="structure-col-segment">{STRUCTURE_COLUMN_LABELS.segment}</th>
                                  {showStructureLocation ? (
                                    <th className="structure-col-location">{STRUCTURE_COLUMN_LABELS.location}</th>
                                  ) : null}
                                  <th className="structure-col-main">{STRUCTURE_COLUMN_LABELS.mainContent}</th>
                                  {showStructureRole ? (
                                    <th className="structure-col-role">{STRUCTURE_COLUMN_LABELS.expressionRole}</th>
                                  ) : null}
                                </tr>
                              </thead>
                              <tbody>
                                {structureRows.map((row, index) => (
                                  <tr key={`${row.segment}-${index}`}>
                                    <td className="structure-col-segment">{row.segment}</td>
                                    {showStructureLocation ? <td className="structure-col-location">{row.location}</td> : null}
                                    <td className="structure-col-main">{row.mainContent}</td>
                                    {showStructureRole ? <td className="structure-col-role">{row.expressionRole}</td> : null}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: activeResult.html || "" }} />
                      )
                    ) : activeResult.status === "pending" ? (
                      <div className="pending-result">
                        <DataIcon name="sparkles" />
                        <h3>正在生成{optionTitle(detail.active, detail.file?.type)}</h3>
                        <p>可以先查看其他结果，完成后会自动显示。</p>
                      </div>
                    ) : (
                      <div className="failure-empty">
                        <DataIcon name="info" />
                        <h3>暂未获得结果</h3>
                        <p>{activeResult.reason || "请重新尝试"}</p>
                      </div>
                    )}
                  </div>
                  <div className="paper-footer">
                    AI生成内容仅供参考，请核对关键事实。
                    <span>{activeResult.plain ? `${[...activeResult.plain].length}字符` : ""}</span>
                  </div>
                </article>
              </div>
            </section>
          )}
        </div>
      </div>

      <dialog
        ref={infoDialogRef}
        onClose={() => setInfoOpen(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget) setInfoOpen(false);
        }}
      >
          <div className="dialog-head">
            <h2>{infoTitle}</h2>
            <button type="button" className="icon-button" aria-label="关闭" onClick={() => setInfoOpen(false)}>
              <DataIcon name="x" />
            </button>
          </div>
          {infoTitle === "导出结果" ? (
            <div>
              <p>导出为 TXT 文件，可保存或继续编辑。</p>
              <div className="export-choices">
                <button
                  type="button"
                  className="secondary"
                  disabled={detail.results[detail.active]?.status !== "success"}
                  onClick={() => downloadResults([detail.active])}
                >
                  导出当前结果
                </button>
                <button type="button" className="primary" onClick={() => downloadResults()}>
                  导出全部已完成结果
                </button>
              </div>
            </div>
          ) : infoTitle === "删除作品" ? (
            <div>
              <p>
                确定删除「{detail.file?.name}」及其全部生成结果？删除后无法恢复。
              </p>
              <div className="delete-actions">
                <button type="button" className="secondary" onClick={() => setInfoOpen(false)}>
                  取消
                </button>
                <button
                  type="button"
                  className="danger-button"
                  onClick={async () => {
                    const id = detail.workId;
                    setInfoOpen(false);
                    if (await removeWork(id)) showToast("作品已删除");
                  }}
                >
                  删除作品
                </button>
              </div>
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: infoHtml }} />
          )}
      </dialog>

      <dialog
          ref={usageDialogRef}
          className="usage-dialog"
          aria-labelledby="usageTitle"
          onClose={() => setUsageOpen(false)}
          onClick={(e) => {
            if (e.target === e.currentTarget) setUsageOpen(false);
          }}
        >
          <header className="usage-header">
            <div>
              <h2 id="usageTitle">
                <DataIcon name="list" />
                AI 转录使用说明
              </h2>
              <p>一次上传一个文件，大小与时长、页数或字符数需同时符合要求。</p>
            </div>
            <button type="button" className="usage-close" aria-label="关闭使用说明" onClick={() => setUsageOpen(false)}>
              <DataIcon name="x" />
            </button>
          </header>
          <div className="usage-body">
            <div className="usage-requirements">
              <section className="usage-item">
                <span className="usage-item-icon video-tone">
                  <DataIcon name="video" />
                </span>
                <div>
                  <h3>视频</h3>
                  <p>
                    支持 <strong>MP4、MOV</strong> 格式
                  </p>
                  <p>
                    大小 ≤ <strong>500MB</strong> · 时长 ≤ <strong>30分钟</strong>
                  </p>
                </div>
              </section>
              <section className="usage-item">
                <span className="usage-item-icon audio-tone">
                  <DataIcon name="headphones" />
                </span>
                <div>
                  <h3>音频</h3>
                  <p>
                    支持 <strong>MP3、WAV、M4A</strong> 格式
                  </p>
                  <p>
                    大小 ≤ <strong>200MB</strong> · 时长 ≤ <strong>120分钟</strong>
                  </p>
                </div>
              </section>
              <section className="usage-item">
                <span className="usage-item-icon document-tone">
                  <DataIcon name="file" />
                </span>
                <div>
                  <h3>文件</h3>
                  <p>
                    <strong>PDF（文字型）</strong>：≤ <strong>20MB</strong> · ≤ <strong>100页</strong> · ≤{" "}
                    <strong>30,000字符</strong>
                  </p>
                  <p>
                    <strong>DOCX</strong>：≤ <strong>20MB</strong> · ≤ <strong>30,000字符</strong>
                  </p>
                  <p>
                    <strong>TXT（UTF-8）</strong>：≤ <strong>1MB</strong> · ≤ <strong>30,000字符</strong>
                  </p>
                </div>
              </section>
            </div>
            <aside className="usage-tips">
              <DataIcon name="info" />
              <div>
                <h3>温馨提示</h3>
                <ul>
                  <li>音视频尽量保持人声清晰，避免明显背景噪音。</li>
                  <li>无声视频可选择画面概述或视频总结。</li>
                  <li>暂不支持扫描件、加密文档及复杂表格解析。</li>
                  <li>处理进度与结果可在「我的作品」中查看。</li>
                </ul>
              </div>
            </aside>
          </div>
        </dialog>

      <dialog
          ref={supplementDialogRef}
          aria-labelledby="supplementTitle"
          onClose={() => {
            setSupplementOpen(false);
            setSupplementDraft(null);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSupplementOpen(false);
              setSupplementDraft(null);
            }
          }}
        >
          <div className="dialog-head">
            <h2 id="supplementTitle">
              {supplementDraft?.mode === "REWRITE_ADJUST"
                ? "调整重写要求"
                : supplementDraft?.force
                  ? `重试${RESULT_META[supplementDraft.force][0]}`
                  : "补充生成"}
            </h2>
            <button
              type="button"
              className="icon-button"
              aria-label="关闭"
              onClick={() => {
                setSupplementOpen(false);
                setSupplementDraft(null);
              }}
            >
              <DataIcon name="x" />
            </button>
          </div>
          <p className="supplement-source">{detail.file?.name}</p>
          <p className="supplement-note">
            {supplementDraft?.force
              ? "生成完成后更新当前结果，失败时保留已有内容。"
              : availableResults(detail.file).filter((k) => detail.results[k]?.status !== "success").length
                ? "只需选择缺少的结果，已有内容会自动复用。"
                : "所有结果均已生成。"}
          </p>
          <div className="options">
            {(supplementDraft?.force
              ? [supplementDraft.force]
              : availableResults(detail.file).filter((k) => detail.results[k]?.status !== "success")
            ).map((k) => (
              <label
                key={k}
                className={`option${supplementDraft?.keys.includes(k) ? " selected" : ""}`}
              >
                <input
                  type="checkbox"
                  value={k}
                  checked={!!supplementDraft?.keys.includes(k)}
                  disabled={!!supplementDraft?.force}
                  onChange={(e) => {
                    if (!supplementDraft) return;
                    setSupplementError("");
                    setSupplementDraft({
                      ...supplementDraft,
                      keys: e.target.checked
                        ? [...supplementDraft.keys, k]
                        : supplementDraft.keys.filter((x) => x !== k),
                    });
                  }}
                />
                <span>{optionTitle(k, detail.file?.type)}</span>
              </label>
            ))}
          </div>
          {supplementDraft?.keys.includes("rewrite") ? (
            <div className="rewrite-field">
              <label htmlFor="supplementPrompt">
                重写要求 <span className="required">*</span>
              </label>
              <textarea
                id="supplementPrompt"
                rows={3}
                maxLength={300}
                placeholder="例如：改成自然的短视频口播，约300字。"
                value={supplementDraft.prompt}
                onChange={(e) => setSupplementDraft({ ...supplementDraft, prompt: e.target.value })}
              />
              <span className="field-note">{supplementDraft.prompt.length} / 300</span>
            </div>
          ) : null}
          {supplementError ? (
            <p className="message error" role="alert">
              {supplementError}
            </p>
          ) : null}
          <div className="supplement-footer">
            <div className="supplement-cost">
              <span
                className={`supplement-points${supplementDraft?.keys.length && supplementQuoteReady ? "" : " is-hidden"}`}
              >
                <CoinsEstimateIcon />
                <span>
                  预计消耗 <span className="cost-point">{supplementQuote.total}</span> 积分
                </span>
              </span>
              <p>合计取整 · 已有基础内容不重复收费</p>
            </div>
            <button
              type="button"
              className="primary"
              disabled={
                !supplementDraft?.keys.length ||
                supplementQuoteLoading ||
                !supplementQuoteReady ||
                (supplementDraft.keys.includes("rewrite") && !supplementDraft.prompt.trim())
              }
              onClick={submitSupplement}
            >
              {supplementDraft?.mode === "REWRITE_ADJUST" ? "重新生成" : "开始生成"}
            </button>
          </div>
        </dialog>

      {toast ? (
        <div role="status" className="toast">
          {toast}
        </div>
      ) : null}
    </div>
  );
};

export default AiTranscription;
